/**
 * API error descriptions — OutpostForge frontend (CES standard)
 * Turns transport/permission failures into actionable messages while
 * passing business errors through untouched.
 *
 * Rule of thumb:
 *   - Transport problems (network down, timeout) and gateway-level
 *     rejections (401/403/429/5xx) are rewritten: the raw axios/backend
 *     text ("Request failed with status 401") tells the user nothing.
 *   - Business errors (validation, not-registered 404, active-task 409,
 *     registration 422, scenarios 503) keep the backend's message: those
 *     are written to be shown as-is and the E2E suite asserts on them.
 */

import { ApiError } from './http';

/** Human, actionable description for an API failure. */
export function describeApiError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'Unexpected error';
  }

  // No HTTP response at all: the API never answered.
  if (err.status === 0) {
    if (err.code === 'TimeoutError') {
      return 'The OutpostForge API took too long to respond. The service may be under load; retry in a moment.';
    }
    return 'Cannot reach the OutpostForge API. Check your network connection (VPN included) and retry.';
  }

  switch (err.status) {
    case 401:
      return 'Your session is not authorized (401). Sign in again; if the problem persists, the Cognito configuration or API key may be wrong.';
    case 403:
      // Backend AccessDeniedException = AWS denied the cross-account role;
      // a gateway 403 (bad API key) has no backend code.
      if (err.code === 'AccessDeniedException') {
        return `AWS denied access to the Outposts data: ${err.message}. Verify the read role is deployed in the owner account and trusts this backend (see the owner-account role doc).`;
      }
      return 'The API rejected the request (403). The API key is missing or invalid for this stage.';
    case 429:
      return 'Too many requests (429): the API throttling limit was hit. Wait a moment and retry.';
    case 500:
    case 502:
    case 504:
      return `The backend could not complete the call (${err.status} ${err.code}): ${err.message}. Retry; if it persists, check the Lambda logs.`;
    default:
      // Business errors (400/404/409/422/503...) are already written for
      // the UI, e.g. "Outpost op-x is not registered in Settings."
      return err.message;
  }
}
